import React from "react";
import { IspEngagementItemView, SubscriberEngagementItemView } from "@tiny/api";
import {
  usePostIspNewSubscriberEngagementMutation,
  usePostNewSubscriberEngagementMutation,
} from "@tiny/api";
import { ChartPoint } from "./EngagerChart";
import { getDateRangeForDays } from "./SegmentPerformanceDialog.helpers";

export interface UseSegmentPerformanceDialogProps {
  open: boolean;
  segmentId: string;
  days: number;
}

export function useSegmentPerformanceDialog({
  open,
  segmentId,
  days,
}: UseSegmentPerformanceDialogProps) {
  const [rows, setRows] = React.useState<SubscriberEngagementItemView[]>([]);
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);
  const [selectedDate, setSelectedDate] = React.useState<number | null>(null);
  const [ispData, setIspData] = React.useState<IspEngagementItemView[]>([]);
  const [ispLoading, setIspLoading] = React.useState(false);
  const requestedDateRef = React.useRef<number | null>(null);

  const { mutateAsync: fetchEngagement, isLoading } =
    usePostNewSubscriberEngagementMutation();

  const { mutateAsync: fetchIspBreakdown } =
    usePostIspNewSubscriberEngagementMutation();

  React.useEffect(() => {
    if (!open || !segmentId) return;
    setIsLoaded(false);
    setRows([]);
    setHoveredIndex(null);
    setSelectedDate(null);
    setIspData([]);
    const { from, to, timeZone } = getDateRangeForDays(days);
    fetchEngagement({ from, to, segmentId, timeZone }).then((data) => {
      const sorted = [...data].sort((a, b) => (b.date ?? 0) - (a.date ?? 0));
      setRows(sorted);
      setIsLoaded(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, segmentId, days]);

  const handleDateClick = React.useCallback(
    (date: number) => {
      if (selectedDate === date) {
        setSelectedDate(null);
        setIspData([]);
        return;
      }
      setSelectedDate(date);
      setIspData([]);
      setIspLoading(true);
      requestedDateRef.current = date;
      const { timeZone } = getDateRangeForDays(days);
      fetchIspBreakdown({ targetDate: date, segmentId, timeZone })
        .then((data) => {
          if (requestedDateRef.current !== date) return;
          setIspData(data);
          setIspLoading(false);
        })
        .catch(() => {
          if (requestedDateRef.current !== date) return;
          setIspLoading(false);
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedDate, segmentId]
  );

  const handleHoverIndex = React.useCallback((index: number | null) => {
    setHoveredIndex(index);
  }, []);

  const totals = React.useMemo(
    () =>
      rows.reduce(
        (
          acc: {
            send: number;
            delivered: number;
            opens: number;
            clicks: number;
          },
          r
        ) => ({
          send: acc.send + (r.send ?? 0),
          delivered: acc.delivered + (r.delivered ?? 0),
          opens: acc.opens + (r.opens ?? 0),
          clicks: acc.clicks + (r.clicks ?? 0),
        }),
        { send: 0, delivered: 0, opens: 0, clicks: 0 }
      ),
    [rows]
  );

  const chartData = React.useMemo<ChartPoint[]>(
    () =>
      rows
        .filter((r) => r.date != null)
        .map((r) => ({
          timestamp: r.date!,
          opens: r.opens ?? 0,
          openRate: r.openRateOnDelivered ?? 0,
        })),
    [rows]
  );

  return {
    rows,
    isLoaded,
    isLoading,
    hoveredIndex,
    handleHoverIndex,
    totals,
    chartData,
    selectedDate,
    ispData,
    ispLoading,
    handleDateClick,
  };
}
